import { useMemo, useState, useEffect, useCallback } from 'react';

const BRANDS_PER_PAGE = 4;

const useStatCardLogic = ({
  isAlert,
  value,
  isClickable,
  onClick,
  showMarcasBreakdown,
  marcasData,
  iconPosition,
}) => {
  const isAlertCard = useMemo(() => isAlert && value > 0, [isAlert, value]);
  const canClick = useMemo(() => isClickable && value > 0, [isClickable, value]);

  const sortedMarcasEntries = useMemo(() => {
    if (!showMarcasBreakdown || !marcasData) return null;
    return Object.entries(marcasData).sort((a, b) => b[1] - a[1]);
  }, [showMarcasBreakdown, marcasData]);

  const totalPages = useMemo(() => {
    if (!sortedMarcasEntries) return 0;
    return Math.ceil(sortedMarcasEntries.length / BRANDS_PER_PAGE);
  }, [sortedMarcasEntries]);

  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [sortedMarcasEntries]);

  const handlePageChange = useCallback((page) => setCurrentPage(page), []);

  const handleClick = useCallback(() => {
    if (canClick && onClick) onClick();
  }, [canClick, onClick]);

  const cardClasses = useMemo(() => {
    const minH = showMarcasBreakdown ? 'min-h-[11.5rem]' : 'min-h-[10.5rem]';
    const base = `rounded-xl shadow-lg hover:shadow-xl transition-shadow ${minH} flex flex-col hover:z-[100] relative`;
    const colorCls = isAlertCard ? 'bg-red-500 text-white' : 'bg-white text-gray-900';
    const clickCls = canClick ? 'cursor-pointer hover:scale-105 transition-transform' : '';
    return iconPosition === 'top'
      ? `${base} ${colorCls} ${clickCls}`
      : `${base} pt-4 px-4 pb-2 justify-between ${colorCls} ${clickCls}`;
  }, [isAlertCard, canClick, iconPosition, showMarcasBreakdown]);

  const titleClasses = useMemo(() =>
    `text-sm font-medium mb-1 ${isAlertCard ? 'text-white' : 'text-gray-600'}`,
    [isAlertCard]
  );

  const valueClasses = useMemo(() => {
    if (iconPosition === 'top') {
      return `text-2xl font-bold leading-tight mr-2 ${isAlertCard ? 'text-white' : 'text-[#1d2e61]'}`;
    }
    return `text-2xl font-bold leading-tight ${isAlertCard ? 'text-white' : 'text-gray-900'}`;
  }, [iconPosition, isAlertCard]);

  return {
    isAlertCard,
    canClick,
    sortedMarcasEntries,
    totalPages,
    currentPage,
    handlePageChange,
    handleClick,
    cardClasses,
    titleClasses,
    valueClasses,
    BRANDS_PER_PAGE,
  };
};

export default useStatCardLogic;
